import {
  Box,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { ErrorModal } from 'components/ErrorModal';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { SuccessModal } from 'components/SuccessModal';
import { ProcessingState } from 'enums/processingState';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { importSessionProcessingStateSelector } from 'redux/reducers/sessionsReducer';
import { SolaceServer, solaceServersSelector } from 'redux/reducers/smConfigReducer';
import {
  fetchSolaceServersAction,
  importSMConfigurationAction,
} from 'redux/services/sessionsServices';

interface Props {
  onClose(): void;
}

interface SolaceServerWithVpn extends SolaceServer {
  readonly vpn?: string;
}

export const ImportModal: React.FC<Props> = (props: Props): React.ReactElement => {
  const [inputValues, setInputValues] = React.useState<Record<string, string>>({
    sessionId: '',
    routerName: '',
    configFileName: '',
  });
  const { onClose } = props;
  const processingState = useSelector(importSessionProcessingStateSelector);
  const dispatch = useDispatch();
  const solaceServers = useSelector(solaceServersSelector);

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      const { name, value } = event.target;
      setInputValues((prev) => ({ ...prev, [name]: value }));
    },
    []
  );
  const handleSelectChange = React.useCallback((event: SelectChangeEvent<string>): void => {
    const { name, value } = event.target;
    setInputValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleImportConfiguration = React.useCallback((): void => {
    const selectedServer: SolaceServerWithVpn | undefined = solaceServers.find(
      (server: SolaceServer): boolean => server.routername === inputValues.routerName
    );

    dispatch(
      importSMConfigurationAction(
        inputValues.sessionId,
        selectedServer?.vpn,
        inputValues.configFileName
      )
    );
  }, [
    dispatch,
    solaceServers,
    inputValues.sessionId,
    inputValues.routerName,
    inputValues.configFileName,
  ]);

  React.useEffect((): void => {
    dispatch(fetchSolaceServersAction());
  }, [dispatch]);

  const empty = React.useMemo((): boolean => {
    const trimmed = inputValues.sessionId.trim();
    return trimmed.length === 0;
  }, [inputValues.sessionId]);

  if (ProcessingState.isError(processingState)) {
    return (
      <ErrorModal
        title="We couldn't import the configuration"
        message="There was an error importing the configuration. Please try again."
        error={processingState}
        onClose={onClose}
      />
    );
  } else if (ProcessingState.isSuccess(processingState)) {
    return (
      <SuccessModal
        title="Imported successfully"
        message={`Configuration for session with id ${inputValues.sessionId} was imported correctly`}
        onClose={onClose}
      />
    );
  } else {
    return (
      <Box position="relative" width={400} padding={2}>
        <Box>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth={true}>
              <FormLabel>Session or Solace ID</FormLabel>
              <OutlinedInput
                onChange={handleInputChange}
                value={inputValues.sessionId}
                name="sessionId"
              />
            </FormControl>
            <FormControl fullWidth={true}>
              <FormLabel>Router Name</FormLabel>
              <Select
                onChange={handleSelectChange}
                value={inputValues.routerName}
                name="routerName"
              >
                {solaceServers.map((server: SolaceServer) => (
                  <MenuItem key={server.routername} value={server.routername}>
                    {server.routername}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth={true}>
              <FormLabel>Config File Name</FormLabel>
              <OutlinedInput
                onChange={handleInputChange}
                value={inputValues.configFileName}
                name="configFileName"
              />
            </FormControl>
          </Box>
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} marginTop={2}>
            <Button color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" disabled={empty} onClick={handleImportConfiguration}>
              Import
            </Button>
          </Box>
        </Box>
        <LoadingOverlay loading={ProcessingState.isProcessing(processingState)} />
      </Box>
    );
  }
};
